import { useEffect, useRef } from 'react';

import ghostImgUrl from './whiteGhost.png';
import { Box } from '@mui/system';
import { keyframes } from '@emotion/react';

// Each ghost fades in/out twice per cycle: once floating in front of the page, once behind the panels.
// z-index only flips while the ghost is fully transparent so the change of plane isn't a visible pop.
const ghostFade = keyframes`
  0% { opacity: 0; z-index: 9999; }
  17.5%, 32.5% { opacity: 0.4; z-index: 9999; }
  49.9% { opacity: 0; z-index: 9999; }
  50% { opacity: 0; z-index: 0; }
  67.5%, 82.5% { opacity: 0.4; z-index: 0; }
  100% { opacity: 0; z-index: 0; }
`;

// pixels per second at size 120 (1 = roughly the original speed)
const GHOST_SPEED = 0.8;

// bigger ghosts move faster and smaller ones slower, so they read as nearer / farther away
const GHOSTS = [
  { size: 80, direction: { dx: 2, dy: 1.5 } },
  { size: 100, direction: { dx: -2.5, dy: 2 } },
  { size: 110, direction: { dx: 1.2, dy: -2.2 } },
  { size: 120, direction: { dx: -1.7, dy: -1 } },
  { size: 130, direction: { dx: -1.5, dy: -1 } },
];

// direction values were originally per-frame at 60fps
const FRAMES_PER_SECOND = 60;

/**
 * Moves the element straight through the DOM (transform) instead of React state. Updating state every frame
 * kept interrupting React while it rendered heavy pages, so they appeared stuck until something else re-rendered.
 */
function useGhostMover(initialDirection: { dx: number; dy: number }, size: number) {
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const speed = GHOST_SPEED * (size / 120) * FRAMES_PER_SECOND;
    let { dx, dy } = initialDirection;
    let x = Math.random() * Math.max(0, window.innerWidth - size);
    let y = Math.random() * Math.max(0, window.innerHeight - size);
    let last = performance.now();
    let frame = 0;

    const animate = (now: number) => {
      // clamp so a background tab doesn't teleport the ghost when it wakes up
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const maxX = Math.max(0, window.innerWidth - size);
      const maxY = Math.max(0, window.innerHeight - size);
      x += dx * speed * dt;
      y += dy * speed * dt;
      if (x < 0 || x > maxX) dx = -dx;
      if (y < 0 || y > maxY) dy = -dy;
      x = Math.max(0, Math.min(x, maxX));
      y = Math.max(0, Math.min(y, maxY));
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [initialDirection, size]);

  return ref;
}

const Ghost = ({ index, size, direction }: { index: number; size: number; direction: { dx: number; dy: number } }) => {
  const ref = useGhostMover(direction, size);
  return (
    <Box
      ref={ref}
      component='img'
      src={ghostImgUrl}
      alt={`Spooky Ghost ${index + 1}`}
      sx={{
        ...ghostStyles,
        width: size,
        height: size,
      }}
    />
  );
};

export const GhostAnimation = () => (
  <>
    {GHOSTS.map((ghost, index) => (
      <Ghost key={index} index={index} {...ghost} />
    ))}
  </>
);

const ghostStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  willChange: 'transform',
  width: 120,
  height: 120,
  opacity: 0,
  // slow fade in/out, staggered so the ghosts don't all appear and vanish together
  animation: `${ghostFade} 32s ease-in-out infinite`,
  '&:nth-of-type(2)': { animationDelay: '-6s' },
  '&:nth-of-type(3)': { animationDelay: '-12s' },
  '&:nth-of-type(4)': { animationDelay: '-18s' },
  '&:nth-of-type(5)': { animationDelay: '-24s' },
  pointerEvents: 'none',
  zIndex: 0,
  transition: 'filter 0.2s',
  filter: 'drop-shadow(0 0 16px orange)',
};
